import React, { useEffect, useMemo, useState } from 'react';
import Loading from '../../../../components/common/Loading/Loading';
import SectionTitle from '../../../../components/common/SectionTitle/SectionTitle';
import {
  EQUIPMENT_CATEGORY_LABELS,
  type EquipmentItem,
} from '../../../../constants/equipmentCategory';
import { StudentEquipmentService } from '../../../../services';
import styles from '../StudentRegister.module.css';
import equipmentStyles from './StudentEquipmentsStep.module.css';

interface StudentEquipmentsStepProps {
  studentId: number;
  isLoading?: boolean;
  titleAction?: React.ReactNode;
  refreshKey?: number;
}

/**
 * Read-only list of equipment selected by the student.
 */
const StudentEquipmentsStep: React.FC<StudentEquipmentsStepProps> = ({
  studentId,
  isLoading: externalLoading = false,
  titleAction,
  refreshKey = 0,
}) => {
  const [items, setItems] = useState<EquipmentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const selected = await StudentEquipmentService.listSelectedForStudent(studentId);
        if (isMounted) {
          setItems(selected);
        }
      } catch (loadError) {
        if (isMounted) {
          setItems([]);
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Não foi possível carregar os equipamentos da aluna.',
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void load();

    return () => {
      isMounted = false;
    };
  }, [studentId, refreshKey]);

  const groups = useMemo(
    () => StudentEquipmentService.groupByCategory(items),
    [items],
  );

  const showLoading = externalLoading || isLoading;

  return (
    <div className={styles.card}>
      <div className={`${styles.sectionHeaderRow} ${styles.sectionHeaderRowTight}`}>
        <SectionTitle as="h2" text="Equipamentos" />
        <div className={styles.photoHeaderActions}>
          {titleAction}
        </div>
      </div>

      {showLoading && (
        <p className={styles.sectionDescription}>Carregando equipamentos...</p>
      )}

      {showLoading ? (
        <Loading />
      ) : error ? (
        <p className={styles.sectionDescription}>{error}</p>
      ) : items.length === 0 ? (
        <p className={styles.sectionDescription}>
          Nenhum equipamento informado ainda. Use o botão “Equipamentos” para copiar o link de seleção.
        </p>
      ) : (
        <div className={equipmentStyles.groups}>
          {groups.map((group) => (
            <section key={group.category} className={equipmentStyles.group}>
              <h3 className={equipmentStyles.groupTitle}>
                {EQUIPMENT_CATEGORY_LABELS[group.category]}
              </h3>
              <ul className={equipmentStyles.list}>
                {group.items.map((item) => (
                  <li key={item.id}>{item.name}</li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default StudentEquipmentsStep;
